// src/components/Navbar.jsx
import { Search, User, Heart, Menu, X, ChevronDown, LogOut, MessageSquare, AlertTriangle } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next'; 
import logo from '../../imports/logo-MORROCARS.png';
import Cookies from 'js-cookie';
import { useWishlist } from '../context/WishlistContext';
import { LayoutDashboard, Tag } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import { NotificationBell } from './NotificationBell';

const isLocalhost = window.location.hostname === 'localhost';

const cookieOptions: Cookies.CookieAttributes = {
  path: '/',
  domain: isLocalhost ? undefined : '.morrocars.com',
  secure: !isLocalhost,
  sameSite: isLocalhost ? 'Lax' : 'None',
};

export function Navbar() {
  const { t } = useTranslation('common'); 
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { wishlistCount, canUseWishlist } = useWishlist();

  // User data state
  const [userData, setUserData] = useState<{
    firstName: string;
    lastName: string;
    email: string;
    customerType: number;
    profilePhoto: string | null;
  }>({
    firstName: '',
    lastName: '',
    email: '',
    customerType: 1,
    profilePhoto: null,
  });

  // Load user data from localStorage/cookies
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('customerUser');
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        setUserData({
          firstName: parsed.firstName || '',
          lastName: parsed.lastName || '',
          email: parsed.email || '',
          customerType: parsed.customerType || 1,
          profilePhoto: parsed.profilePhoto || null,
        });
      } else {
        const userCookie = Cookies.get('user');
        if (userCookie) {
          const parsed = JSON.parse(userCookie);
          setUserData({
            firstName: parsed.firstName || '',
            lastName: parsed.lastName || '',
            email: parsed.email || '',
            customerType: parsed.customerType || 1,
            profilePhoto: parsed.profilePhoto || null,
          });
        }
      }
    } catch (error) {
      console.error('Failed to load user data:', error);
    }
  }, []);
  useEffect(() => {
  const handleClickOutside = (event: MouseEvent) => {
    if (
      profileRef.current &&
      !profileRef.current.contains(event.target as Node)
    ) {
      setProfileOpen(false);
    }
  };

  if (profileOpen) {
    document.addEventListener('mousedown', handleClickOutside);
  }

  return () => {
    document.removeEventListener('mousedown', handleClickOutside);
  };
}, [profileOpen]);
useEffect(() => {
  const checkAuth = () => {
    const token = Cookies.get('token');

    setIsLoggedIn(!!token);

    if (!token) {
      setUserData({
        firstName: '',
        lastName: '',
        email: '',
        customerType: 1,
        profilePhoto: null,
      });
    }
  };

  window.addEventListener(
    'morrocars-auth-changed',
    checkAuth
  );

  return () => {
    window.removeEventListener(
      'morrocars-auth-changed',
      checkAuth
    );
  };
}, []);

  const [isLoggedIn, setIsLoggedIn] = useState(
  !!Cookies.get('token')
);
  const customerType = userData.customerType;

const handleLogout = () => {
  // Remove cookies using SAME domain/path used while setting them
  Cookies.remove('token', cookieOptions);
  Cookies.remove('customerType', cookieOptions);
  Cookies.remove('user', cookieOptions);

  // Remove local user data
  localStorage.removeItem('customerUser');

  // Clear React state immediately
  setIsLoggedIn(false);

  setUserData({
    firstName: '',
    lastName: '',
    email: '',
    customerType: 1,
    profilePhoto: null,
  });

  // Notify other components
  window.dispatchEvent(
    new Event('morrocars-auth-changed')
  );

  setIsMenuOpen(false);
  setProfileOpen(false);

  navigate('/auth/login', { replace: true });
};

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
  };

  // Helper functions for user display
  const getInitials = () => {
    const first = userData.firstName?.charAt(0) || 'U';
    const last = userData.lastName?.charAt(0) || '';
    return (first + last).toUpperCase();
  };

  const getFullName = () => {
    return `${userData.firstName} ${userData.lastName}`.trim() || 'User';
  };

  // Role-based menu items
  const isBuyer = customerType === 1;
  const isSeller = customerType === 2;
  const isDealer = customerType === 3;

  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-border-gray shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[88px]">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" onClick={() => setIsMenuOpen(false)}>
              <img
                src={logo}
                alt="MORROCARS"
                className="w-[125px] sm:w-[135px] md:w-[140px] lg:w-[145px] xl:w-[155px] h-auto object-contain"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4 lg:gap-5 ml-5 xl:ml-8 shrink-0">
            <Link
              to="/search"
              className="whitespace-nowrap text-[#2D323A] hover:text-[#1E6DFF] transition-colors"
            >
              {t('nav.buy')}
            </Link>

            <Link
              to="/about"
              className="whitespace-nowrap text-[#2D323A] hover:text-[#1E6DFF] transition-colors"
            >
              {t('nav.about')}
            </Link>

            <Link
              to="/contact"
              className="whitespace-nowrap text-[#2D323A] hover:text-[#1E6DFF] transition-colors"
            >
              {t('nav.contact')}
            </Link>
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-2 lg:gap-3 xl:gap-4 ml-auto min-w-0">
            {/* Search */}
            <form
              onSubmit={handleSearch}
              className="relative shrink min-w-[150px] lg:min-w-[170px] xl:min-w-[190px]"
            >
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#717182]" />

              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('nav.search_placeholder')}
                className="
                  w-full
                  pl-9 pr-3
                  py-2
                  bg-[#F5F7FA]
                  border border-[#E5E7EB]
                  rounded-xl
                  text-sm
                  focus:outline-none
                  focus:border-[#1E6DFF]
                  transition-all
                "
              />
            </form>

            {canUseWishlist && (
              <Link
                to="/wishlist"
                className="relative p-2 text-[#2D323A] hover:text-[#1E6DFF] transition-colors"
                title={t('nav.wishlist')}
              >
                <Heart className="w-5 h-5" />
                {wishlistCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#1E6DFF] text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                    {wishlistCount}
                  </span>
                )}
              </Link>
            )}
            {isLoggedIn && isBuyer && (
              <div className="flex items-center">
                <NotificationBell />
              </div>
            )}

            <div className="flex items-center shrink-0">
              <LanguageSwitcher />
            </div>

            {/* User Dropdown */}
            {isLoggedIn ? (
              <div ref={profileRef} className="relative">
                <button
                  className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-[#F5F7FA] transition-colors"
                  onClick={() => setProfileOpen(!profileOpen)}
                >
                  {userData.profilePhoto ? (
                    <img
                      src={userData.profilePhoto}
                      alt="Profile"
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 bg-[#1E6DFF] rounded-full flex items-center justify-center text-white text-sm font-medium">
                      {getInitials()}
                    </div>
                  )}
                  <span className="hidden lg:block max-w-[120px] xl:max-w-[150px] truncate whitespace-nowrap text-sm text-[#2D323A]">{getFullName()}</span>
                  <ChevronDown className="w-4 h-4 text-[#717182]" />
                </button>

                {profileOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-[#E5E7EB] rounded-xl shadow-lg py-2 z-50">
                    <div className="px-4 py-2 border-b border-[#E5E7EB]">
                      <p className="text-sm font-medium text-[#2D323A]">{getFullName()}</p>
                      <p className="text-xs text-[#717182] truncate">{userData.email}</p>
                    </div>

                    {isBuyer && (
                      <>
                        <Link
                          to="/messages"
                          className="flex items-center gap-2 px-4 py-2 text-sm text-[#2D323A] hover:bg-[#F5F7FA]"
                          onClick={() => setProfileOpen(false)}
                        >
                          <MessageSquare className="w-4 h-4" />
                          {t('nav.profile.messages')}
                        </Link>
                        <Link
                          to="/complaints"
                          className="flex items-center gap-2 px-4 py-2 text-sm text-[#2D323A] hover:bg-[#F5F7FA]"
                          onClick={() => setProfileOpen(false)}
                        >
                          <AlertTriangle className="w-4 h-4" />
                          {t('nav.profile.complaints')}
                        </Link>
                      </>
                    )}

                    {isSeller && (
                      <>
                        <Link
                          to="/dashboard"
                          className="flex items-center gap-2 px-4 py-2 text-sm text-[#2D323A] hover:bg-[#F5F7FA]"
                          onClick={() => setProfileOpen(false)}
                        >
                          <LayoutDashboard className="w-4 h-4" />
                          {t('nav.profile.dashboard')}
                        </Link>
                        <Link
                          to="/dashboard/profile"
                          className="flex items-center gap-2 px-4 py-2 text-sm text-[#2D323A] hover:bg-[#F5F7FA]"
                          onClick={() => setProfileOpen(false)}
                        >
                          <User className="w-4 h-4" />
                          {t('nav.profile.profile')}
                        </Link>
                        <Link
                          to="/dashboard/subscription"
                          className="flex items-center gap-2 px-4 py-2 text-sm text-[#2D323A] hover:bg-[#F5F7FA]"
                          onClick={() => setProfileOpen(false)}
                        >
                          <Tag className="w-4 h-4" />
                          {t('nav.profile.subscription')}
                        </Link>
                      </>
                    )}

                    {isDealer && (
                      <>
                        <Link
                          to="/dealer"
                          className="flex items-center gap-2 px-4 py-2 text-sm text-[#2D323A] hover:bg-[#F5F7FA]"
                          onClick={() => setProfileOpen(false)}
                        >
                          <LayoutDashboard className="w-4 h-4" />
                          {t('nav.profile.dashboard')}
                        </Link>
                        <Link
                          to="/dealer/settings"
                          className="flex items-center gap-2 px-4 py-2 text-sm text-[#2D323A] hover:bg-[#F5F7FA]"
                          onClick={() => setProfileOpen(false)}
                        >
                          <User className="w-4 h-4" />
                          {t('nav.profile.profile')}
                        </Link>
                        <Link
                          to="/dealer/subscription"
                          className="flex items-center gap-2 px-4 py-2 text-sm text-[#2D323A] hover:bg-[#F5F7FA]"
                          onClick={() => setProfileOpen(false)}
                        >
                          <Tag className="w-4 h-4" />
                          {t('nav.profile.subscription')}
                        </Link>
                      </>
                    )}

                    <hr className="my-1 border-[#E5E7EB]" />

                    <button
                      onClick={() => {
                        handleLogout();
                        setProfileOpen(false);
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-[#EF4444] hover:bg-[#F5F7FA] text-left"
                    >
                      <LogOut className="w-4 h-4" />
                      {t('nav.sign_out')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/auth/login"
                className="px-3 lg:px-4 py-2 text-[#2D323A] hover:text-[#1E6DFF] transition-colors inline-flex items-center gap-2"
              >
                <User className="w-5 h-5" />
                {t('nav.sign_in')}
              </Link>
            )}

            {(!isLoggedIn || !isBuyer) && (
              <Link
                to="/sell"
                className="
                  px-4 lg:px-5 xl:px-6
                  py-2.5
                  bg-[#1E6DFF]
                  text-white
                  rounded-xl
                  hover:bg-[#1557d9]
                  transition-colors
                  shadow-md
                  hover:shadow-lg
                  whitespace-nowrap
                  shrink-0
                "
              >
                {t('nav.list_vehicle')}
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            type="button"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
            className="md:hidden ml-auto flex-shrink-0 p-2 text-[#2D323A] hover:text-[#1E6DFF] transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden border-t border-[#E5E7EB] bg-white shadow-md">
          <div className="px-4 py-4 space-y-2">
            <form onSubmit={handleSearch} className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#717182]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('nav.search_placeholder')}
                className="w-full pl-9 pr-4 py-2.5 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl text-sm focus:outline-none focus:border-[#1E6DFF]"
              />
            </form>

            <Link to="/search" className="block py-2.5 text-[#2D323A] hover:text-[#1E6DFF]" onClick={() => setIsMenuOpen(false)}>
              {t('nav.buy')}
            </Link>
            <Link to="/about" className="block py-2.5 text-[#2D323A] hover:text-[#1E6DFF]" onClick={() => setIsMenuOpen(false)}>
              {t('nav.about')}
            </Link>
            <Link to="/contact" className="block py-2.5 text-[#2D323A] hover:text-[#1E6DFF]" onClick={() => setIsMenuOpen(false)}>
              {t('nav.contact')}
            </Link>

            <div className="pt-3 mt-2 space-y-3 border-t border-[#E5E7EB]">
              {canUseWishlist && (
                <Link
                  to="/wishlist"
                  className="flex items-center gap-2 py-2.5 text-[#2D323A] hover:text-[#1E6DFF]"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <div className="relative flex items-center">
                    <Heart className="w-4 h-4" />
                    {wishlistCount > 0 && (
                      <span className="ml-1 bg-[#1E6DFF] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                        {wishlistCount}
                      </span>
                    )}
                  </div>
                  {t('nav.wishlist')}
                </Link>
              )}
              {isLoggedIn && isBuyer && (
                <NotificationBell />
              )}
              {isLoggedIn ? (
                <>
                  {isBuyer && (
                    <>
                      <Link
                        to="/messages"
                        className="block py-2.5 text-[#2D323A] hover:text-[#1E6DFF]"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {t('nav.profile.messages')}
                      </Link>
                      <Link
                        to="/complaints"
                        className="block py-2.5 text-[#2D323A] hover:text-[#1E6DFF]"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {t('nav.profile.complaints')}
                      </Link>
                    </>
                  )}

                  {isSeller && (
                    <>
                      <Link to="/dashboard" className="block py-2.5 text-[#2D323A] hover:text-[#1E6DFF]" onClick={() => setIsMenuOpen(false)}>
                        {t('nav.profile.dashboard')}
                      </Link>
                      <Link to="/dashboard/profile" className="block py-2.5 text-[#2D323A] hover:text-[#1E6DFF]" onClick={() => setIsMenuOpen(false)}>
                        {t('nav.profile.profile')}
                      </Link>
                      <Link to="/dashboard/subscription" className="block py-2.5 text-[#2D323A] hover:text-[#1E6DFF]" onClick={() => setIsMenuOpen(false)}>
                        {t('nav.profile.subscription')}
                      </Link>
                    </>
                  )}

                  {isDealer && (
                    <>
                      <Link to="/dealer" className="block py-2.5 text-[#2D323A] hover:text-[#1E6DFF]" onClick={() => setIsMenuOpen(false)}>
                        {t('nav.profile.dashboard')}
                      </Link>
                      <Link to="/dealer/settings" className="block py-2.5 text-[#2D323A] hover:text-[#1E6DFF]" onClick={() => setIsMenuOpen(false)}>
                        {t('nav.profile.profile')}
                      </Link>
                      <Link to="/dealer/subscription" className="block py-2.5 text-[#2D323A] hover:text-[#1E6DFF]" onClick={() => setIsMenuOpen(false)}>
                        {t('nav.profile.subscription')}
                      </Link>
                    </>
                  )}

                  <button
                    onClick={handleLogout}
                    className="block w-full px-4 py-2.5 text-center text-[#EF4444] border border-[#EF4444] rounded-xl hover:bg-[#FEF2F2] transition-colors"
                  >
                    {t('nav.sign_out')}
                  </button>
                </>
              ) : (
                <Link
                  to="/auth/login"
                  className="block w-full px-4 py-2.5 text-center text-[#2D323A] border border-[#E5E7EB] rounded-xl hover:border-[#1E6DFF] transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('nav.sign_in')}
                </Link>
              )}

              {(!isLoggedIn || !isBuyer) && (
                <Link
                  to="/sell"
                  className="block w-full px-4 py-2.5 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors text-center"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('nav.list_vehicle')}
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}